import { useSyncExternalStore } from "react";
import { canonicalProjectUrl, parseRoute, type Route } from "./routes.ts";

export type { Route } from "./routes.ts";
export { projectPath } from "./routes.ts";

/** Tiny history-API router; enough for a handful of screens without adding a routing framework. */
const listeners = new Set<() => void>();
let current = typeof location !== "undefined" ? location.pathname + location.search : "/";

/** A retired URL (/m/:id/w/:id) is replaced by its canonical Project URL before anything renders. */
function redirectRetired(): void {
  if (typeof location === "undefined") return;
  const target = canonicalProjectUrl(location.pathname, location.search);
  if (target) {
    history.replaceState(null, "", target);
    current = target;
  }
}
if (typeof window !== "undefined") {
  redirectRetired();
}

/** Returns false to cancel a navigation (used to protect unsaved editor state). */
type Guard = (targetPath: string) => boolean;
let guard: Guard | null = null;

/** Registers the single active navigation guard; returns the function that removes it. */
export function setNavigationGuard(g: Guard): () => void {
  guard = g;
  return () => {
    if (guard === g) guard = null;
  };
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    const next = location.pathname + location.search;
    // Browser Back/Forward cannot be cancelled, so a refused navigation is undone by restoring the previous URL.
    if (guard && !guard(next)) {
      history.pushState(null, "", current);
      return;
    }
    current = next;
    redirectRetired();
    listeners.forEach((l) => l());
  });
}

export function navigate(path: string): boolean {
  if (location.pathname + location.search === path) return true;
  if (guard && !guard(path)) return false;
  history.pushState(null, "", path);
  current = path;
  listeners.forEach((l) => l());
  return true;
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

let cachedKey = "";
let cachedRoute: Route = { name: "home" };
function snapshot(): Route {
  if (typeof location === "undefined") {
    return { name: "home" };
  }
  const key = location.pathname + location.search;
  if (key !== cachedKey) {
    cachedKey = key;
    cachedRoute = parseRoute(location.pathname, location.search);
  }
  return cachedRoute;
}

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
